import { useMemo, useState } from 'react';

import {
  TrackAssessmentCategorySlug,
  TrackAssessmentDimension,
  TrackAssessmentDimScore,
  TrackAssessmentResult,
  DimensionScoreLevelName,
  Tier,
  TIERS,
  TRACK_CODE_ORDER,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useSurveyData } from '@backrs_ui/grow-ui/src/hooks/useSurveyData';
import { ProfileFields } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import {
  useGetDimensionsQuery,
  useGetDimensionCriteriaQuery,
  useGetTrackAssessmentStandardsQuery,
  useUpdateDimensionScoreMutation,
} from '@backrs_ui/grow-ui/src/redux/trackAssessment/trackAssessment.api';

import type {
  AssessmentBreakdownData,
  CategoryScoresData,
  DimensionItem,
  EditingDimensionData,
} from '../assessMyTrack.types';
import { getMostRigorousMajorLevel, getSchoolSelectivityLevel } from '../assessMyTrack.utils';

const RIGOR_LEVELS = appStrings.modules.assessMyTrack.rigorLevels;
const BREAKDOWN_STRINGS = appStrings.modules.assessMyTrack.assessmentBreakdown;

const getCourseworkRigorScore = (rigorName: string | undefined): DimensionScoreLevelName => {
  if (!rigorName) return 'unmet';

  const name = rigorName.toLowerCase();

  if (name === 'high' || name === 'very high') return 'advanced';
  if (name === 'medium') return 'minimum';

  return 'unmet';
};

const getTestScoreLevel = (result: TrackAssessmentResult): DimensionScoreLevelName => {
  if (!result.sat_score && !result.act_score) return 'unmet';

  const thresholds = result.test_score_thresholds;
  const trackCode = result.careerTrack?.code;

  if (!thresholds || !trackCode) return 'unmet';

  const scores: boolean[] = [];

  if (result.sat_score && thresholds.sat[trackCode]) {
    const { floor, ceiling } = thresholds.sat[trackCode];
    const midpoint = (floor + (ceiling ?? 1600)) / 2;

    scores.push(result.sat_score >= midpoint);
  }

  if (result.act_score && thresholds.act[trackCode]) {
    const { floor, ceiling } = thresholds.act[trackCode];
    const midpoint = (floor + (ceiling ?? 36)) / 2;

    scores.push(result.act_score >= midpoint);
  }

  // If any score is in the upper half of the band, green
  return scores.some(Boolean) ? 'advanced' : 'minimum';
};

const getGpaScore = (result: TrackAssessmentResult): DimensionScoreLevelName => {
  if (!result.gpa_thresholds?.gpa_thresholds || !result.careerTrack?.code) return 'minimum';

  const thresholds = result.gpa_thresholds.gpa_thresholds;
  const currentCode = result.careerTrack.code;
  const currentIdx = TRACK_CODE_ORDER.indexOf(currentCode);
  const currentThreshold = thresholds[currentCode];

  if (currentThreshold == null) return 'minimum';

  const nextCode = TRACK_CODE_ORDER[currentIdx + 1];
  const ceiling = nextCode ? thresholds[nextCode] : 4.0;
  const midpoint = (currentThreshold + (ceiling ?? 4.0)) / 2;

  return result.gpa >= midpoint ? 'advanced' : 'minimum';
};

const getCollegeSelectivityScore = (result: TrackAssessmentResult): DimensionScoreLevelName => {
  const selectivity = result.college?.selectivity;

  if (!selectivity || selectivity === 1) return 'unmet';
  if (selectivity === 2) return 'minimum';

  return 'advanced';
};

const getMajorRigorScoreLevel = (result: TrackAssessmentResult): DimensionScoreLevelName => {
  if (!result.academicMajors || result.academicMajors.length === 0) return 'unmet';

  const level = getMostRigorousMajorLevel(result.academicMajors);

  if (level === RIGOR_LEVELS.veryRigorous) return 'advanced';
  if (level === RIGOR_LEVELS.veryLowRigor) return 'unmet';

  return 'minimum';
};

const formatTestScoreDescription = (result: TrackAssessmentResult): string | undefined => {
  const parts = [
    result.sat_score && `SAT: ${Math.trunc(result.sat_score)}`,
    result.act_score && `ACT: ${Math.trunc(result.act_score)}`,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : BREAKDOWN_STRINGS.noTestScoresReported;
};

const getAcademicClientSideDimensions = (result: TrackAssessmentResult): DimensionItem[] => {
  const isHighSchool = result.enrollment_type_level === 'high_school';

  if (isHighSchool) {
    const courseworkRigor = getCourseworkRigorScore(result.academicRigor?.name);
    const testScores = getTestScoreLevel(result);
    const gpa = getGpaScore(result);

    return [
      {
        id: 0,
        scoreId: 0,
        name: BREAKDOWN_STRINGS.courseworkRigor,
        score: courseworkRigor,
        editType: 'profile-field',
        profileFieldKey: 'academicRigor',
        description: result.academicRigor?.name,
        lubavScore: courseworkRigor,
      },
      {
        id: 0,
        scoreId: 0,
        name: BREAKDOWN_STRINGS.testScores,
        score: testScores,
        editType: 'profile-field',
        profileFieldKey: ['sat_score', 'act_score'] as ProfileFields[],
        description: formatTestScoreDescription(result),
        lubavScore: testScores,
      },
      {
        id: 0,
        scoreId: 0,
        name: BREAKDOWN_STRINGS.gpa,
        score: gpa,
        editType: 'profile-field',
        profileFieldKey: 'high_school_gpa',
        description: result.gpa
          ? BREAKDOWN_STRINGS.gpaDescription(result.gpa)
          : BREAKDOWN_STRINGS.noGpaReported,
        lubavScore: gpa,
      },
    ];
  }

  const collegeSelectivity = getCollegeSelectivityScore(result);
  const majorRigor = getMajorRigorScoreLevel(result);
  const gpa = getGpaScore(result);

  return [
    {
      id: 0,
      scoreId: 0,
      name: BREAKDOWN_STRINGS.collegeSelectivity,
      score: collegeSelectivity,
      editType: 'profile-field',
      profileFieldKey: 'academicsPostSecondaryCurrentEnrollment',
      description: getSchoolSelectivityLevel(result),
      lubavScore: collegeSelectivity,
    },
    {
      id: 0,
      scoreId: 0,
      name: BREAKDOWN_STRINGS.majorRigor,
      score: majorRigor,
      editType: 'profile-field',
      profileFieldKey: 'academicMajors',
      description: result.academicMajors?.length
        ? getMostRigorousMajorLevel(result.academicMajors)
        : undefined,
      lubavScore: majorRigor,
    },
    {
      id: 0,
      scoreId: 0,
      name: BREAKDOWN_STRINGS.gpa,
      score: gpa,
      editType: 'profile-field',
      profileFieldKey: 'post_secondary_gpa',
      description: result.gpa
        ? BREAKDOWN_STRINGS.gpaDescription(result.gpa)
        : BREAKDOWN_STRINGS.noGpaReported,
      lubavScore: gpa,
    },
  ];
};

const getAcademicCategoryScores = (
  result: TrackAssessmentResult,
  dimensionScores: TrackAssessmentDimScore[],
): CategoryScoresData | null => {
  const clientSideItems = getAcademicClientSideDimensions(result);

  // Pull LLM-scored academic items directly from scores (each score embeds its dimension data)
  const llmItems: DimensionItem[] = dimensionScores
    .filter((s) => s.dimension.category.slug === 'academic-foundations')
    .sort((a, b) => a.dimension.display_order - b.dimension.display_order)
    .map((score) => ({
      id: score.dimension_id,
      scoreId: score.id,
      name: score.dimension.name,
      score: score.overrideScoreLevel?.name ?? score.scoreLevel.name,
      editType: 'dimension-toggle' as const,
      lubavScore: score.scoreLevel.name,
    }));

  const allDimensions = [...clientSideItems, ...llmItems];

  if (allDimensions.length === 0) return null;

  const categoryName = llmItems[0]
    ? (dimensionScores.find((s) => s.dimension.category.slug === 'academic-foundations')?.dimension
        .category.shortname ?? BREAKDOWN_STRINGS.academics)
    : BREAKDOWN_STRINGS.academics;

  return {
    categoryName,
    categorySlug: 'academic-foundations',
    dimensions: allDimensions,
  };
};

const getCategoryScores = (
  categorySlug: TrackAssessmentCategorySlug,
  dimensions: TrackAssessmentDimension[],
  dimensionScores: TrackAssessmentDimScore[],
): CategoryScoresData => {
  const categoryDimensions = dimensions.filter((d) => d.category.slug === categorySlug);

  return {
    categoryName: categoryDimensions[0]?.category.shortname ?? '',
    categorySlug,
    dimensions: categoryDimensions.flatMap((dim) => {
      const score = dimensionScores.find((s) => s.dimension_id === dim.id);

      if (!score) return [];

      return [
        {
          id: dim.id,
          scoreId: score.id,
          name: dim.name,
          score: score.overrideScoreLevel?.name ?? score.scoreLevel.name,
          editType: 'dimension-toggle' as const,
          lubavScore: score.scoreLevel.name,
        },
      ];
    }),
  };
};

/**
 * The assessment's owner, when it is not the signed-in user's own. Naming them makes an
 * absent tier mean "no blueprint" rather than falling back to the signed-in user's.
 */
export type BreakdownSubject = Readonly<{ minimumTrack: Tier | undefined }>;

/** The profile-backed dimension being looked at, and the value this assessment recorded for it. */
export type EditingProfileField = Readonly<{
  fieldKey: ProfileFields | ProfileFields[];
  value: string | undefined;
}>;

export const useAssessmentBreakdown = (subject?: BreakdownSubject) => {
  const [editingDimension, setEditingDimension] = useState<EditingDimensionData>(null);
  const [editingProfileField, setEditingProfileField] = useState<EditingProfileField | null>(null);

  const { data: dimensions } = useGetDimensionsQuery(undefined);
  const { data: dimensionCriteria } = useGetDimensionCriteriaQuery({});
  const { data: standards } = useGetTrackAssessmentStandardsQuery(undefined);
  const [
    updateDimensionScore,
    { isLoading: isSubmitting, error: submitError, reset: resetMutation },
  ] = useUpdateDimensionScoreMutation();

  const isReadOnly = !!subject;

  const { surveyResults } = useSurveyData();
  const minimumTrack = subject ? subject.minimumTrack : surveyResults?.tier;

  // trades/middle → Minimum criteria, upper/top → Advanced criteria
  const criteriaLevelToShow: 'Minimum' | 'Advanced' =
    minimumTrack === TIERS.UPPER || minimumTrack === TIERS.TOP ? 'Advanced' : 'Minimum';

  const getBreakdownForAssessment = useMemo(() => {
    return (
      assessmentResult: TrackAssessmentResult | undefined,
    ): AssessmentBreakdownData | null => {
      if (!assessmentResult?.dimensionScores || !dimensions) return null;

      const { dimensionScores } = assessmentResult;

      const academics = getAcademicCategoryScores(assessmentResult, dimensionScores);

      return {
        ...(academics && { 'academic-foundations': academics }),
        'real-world-experience': getCategoryScores(
          'real-world-experience',
          dimensions,
          dimensionScores,
        ),
        'mindsets-and-skills': getCategoryScores(
          'mindsets-and-skills',
          dimensions,
          dimensionScores,
        ),
        'community-of-success': getCategoryScores(
          'community-of-success',
          dimensions,
          dimensionScores,
        ),
      };
    };
  }, [dimensions]);

  const makeCriteriaDescriptionGetter = useMemo(() => {
    return (
      gradeId: number | undefined,
      useShortText = false,
      careerTrackId?: number,
      tenure?: 'High School' | 'Post High School',
    ) => {
      return (
        categorySlug: TrackAssessmentCategorySlug,
        dimensionName: string,
        levelOverride?: 'Minimum' | 'Advanced',
      ): string | undefined => {
        // For academic dimensions, use standards instead of criteria
        if (categorySlug === 'academic-foundations') {
          if (!standards || !careerTrackId || !tenure) return undefined;

          const standard = standards.find(
            (s) =>
              s.dimension_name === dimensionName &&
              s.tenure === tenure &&
              s.careerTrack?.id === careerTrackId,
          );

          return standard?.standard_text;
        }

        if (!dimensionCriteria || !dimensions || !gradeId) return undefined;

        const dimension = dimensions.find(
          (d) =>
            d.category.slug === categorySlug &&
            d.name.toLowerCase() === dimensionName.toLowerCase(),
        );

        if (!dimension) return undefined;

        const level = levelOverride ?? criteriaLevelToShow;

        const criteria = dimensionCriteria.find(
          (c) =>
            c.dimension_id === dimension.id && c.academic_grade_id === gradeId && c.level === level,
        );

        if (useShortText) {
          return criteria?.criteria_text_short ?? criteria?.criteria_text;
        }

        return criteria?.criteria_text;
      };
    };
  }, [dimensionCriteria, dimensions, criteriaLevelToShow, standards]);

  const handleDimensionClick = (
    categorySlug: TrackAssessmentCategorySlug,
    dimension: DimensionItem,
  ) => {
    if (dimension.editType === 'profile-field' && dimension.profileFieldKey) {
      setEditingProfileField({
        fieldKey: dimension.profileFieldKey,
        value: dimension.description,
      });

      return;
    }

    setEditingDimension({
      categorySlug: categorySlug as keyof AssessmentBreakdownData,
      dimensionName: dimension.name,
      currentScore: dimension.score,
      scoreId: dimension.scoreId,
      lubavScore: dimension.lubavScore,
    });
  };

  const handleDimensionSubmit = async (
    newScore: DimensionScoreLevelName,
    justification: string,
  ) => {
    if (!editingDimension || isReadOnly) return;

    try {
      await updateDimensionScore({
        scoreId: editingDimension.scoreId,
        scoreLevel: newScore,
        justification: justification || undefined,
      }).unwrap();
      setEditingDimension(null);
    } catch {
      // error is handled by the mutation's error handling
    }
  };

  const clearEditingDimension = () => {
    setEditingDimension(null);
    resetMutation();
  };

  return {
    getBreakdownForAssessment,
    makeCriteriaDescriptionGetter,
    minimumTrack,
    isReadOnly,

    editingDimension,
    clearEditingDimension,
    handleDimensionClick,
    handleDimensionSubmit,
    isSubmitting,
    submitError,

    editingProfileField,
    setEditingProfileField,
  };
};
